import { useEffect, useState } from 'react';

import Select, { MultiValue } from 'react-select';

import { ISelectMultiOption, reactSelectMultiStyles } from '@shared/components/Form';
import { TTableColumnsFilter, useTableContext } from '@shared/components/Table';

import { MenuList, Option, ValueContainer } from './FilterSelectAsync';

interface IOption {
  id: string;
  label: string;
}

interface IProps {
  name: string;
  options: IOption[];
}

export const FilterSelectMulti = <T,>({ name, options }: IProps) => {
  const { currentFilter, updateFilter, deleteFilterKey } = useTableContext<T>();
  const filterKey = `filter[${String(name)}][]`;
  const currentFilterValue = currentFilter.getAll(filterKey);

  const selectOptions: ISelectMultiOption[] = options.map(o => ({ value: o.id, label: o.label }));

  const [selectedOptions, setSelectedOptions] = useState<MultiValue<ISelectMultiOption>>(
    currentFilterValue.map((value: string) => ({
      value,
      label: options.find(o => o.id === value)?.label ?? value,
    })),
  );

  useEffect(() => {
    if (selectedOptions.length !== 0 && currentFilterValue.length === 0) {
      setSelectedOptions([]);
    }
  }, [currentFilterValue, selectedOptions]);

  const handleChange = (selected: MultiValue<ISelectMultiOption> | null) => {
    setSelectedOptions(selected as ISelectMultiOption[]);

    if (!selected || selected.length === 0) {
      deleteFilterKey(filterKey);
      return;
    }

    const newFilter = {} as Record<string, string>;
    selected.forEach((option, index) => {
      newFilter[`${filterKey}[${index}]`] = option.value;
    });
    updateFilter(newFilter as TTableColumnsFilter<T>);
  };

  return (
    <Select
      className='table-filter-select-multi'
      components={{
        DropdownIndicator: null,
        IndicatorSeparator: null,
        MenuList,
        Option,
        ValueContainer,
      }}
      isMulti
      isClearable
      hideSelectedOptions={false}
      maxMenuHeight={200}
      menuPosition='fixed'
      menuShouldBlockScroll={true}
      menuShouldScrollIntoView={false}
      onChange={handleChange}
      options={selectOptions}
      styles={{
        ...reactSelectMultiStyles,
        control: (provided, state) => ({
          ...(reactSelectMultiStyles.control?.(provided, state) ?? provided),
          minWidth: '160px',
          height: '44px',
        }),
      }}
      value={selectedOptions}
      closeMenuOnSelect={false}
    />
  );
};
